import { useCallback, useState } from 'react'

/* -----------------------------------------------------------------------------
 * component states
 *
 * @walrus/component-states: wraps a pair of handlers (onFocus/onBlur,
 * onMouseEnter/onMouseLeave, onPressIn/onPressOut) to track whether the state
 * is on, and returns the rest of the props without them.
 * -------------------------------------------------------------------------- */

type Handler = ((event: any) => void) | undefined

const useComponentState = (
  onProp: string,
  offProp: string,
  onHandler: Handler,
  offHandler: Handler
) => {
  const [state, setState] = useState(false)
  const on = useCallback(
    (event: any) => {
      setState(true)
      onHandler?.(event)
    },
    [onHandler]
  )
  const off = useCallback(
    (event: any) => {
      setState(false)
      offHandler?.(event)
    },
    [offHandler]
  )
  const apply = <Val>(value: Val) => (state ? value : undefined)
  return [{ [onProp]: on, [offProp]: off }, apply] as const
}

const definitions = {
  focus: ['onFocus', 'onBlur'],
  hover: ['onMouseEnter', 'onMouseLeave'],
  press: ['onPressIn', 'onPressOut']
} as const

type State = keyof typeof definitions
export type ApplyFn = <Val>(value: Val) => Val | undefined

export const useComponentStates = <Props extends Record<string, any>>(
  props: Props,
  states: State[]
) => {
  const handlers: Record<string, (event: any) => void> = {}
  const applied = {} as Record<State, ApplyFn>
  const rest: Record<string, any> = { ...props }
  // the same hooks run in the same order on every render, as in the original
  ;(['focus', 'hover', 'press'] as State[]).forEach(state => {
    if (!states.includes(state)) return
    const [onProp, offProp] = definitions[state]
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [listeners, apply] = useComponentState(
      onProp,
      offProp,
      props[onProp],
      props[offProp]
    )
    Object.assign(handlers, listeners)
    applied[state] = apply
    delete rest[onProp]
    delete rest[offProp]
  })
  return [handlers, applied, rest] as const
}
